import React, { useEffect, useMemo } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import TableContainer from "./TableContainer";
import { useFilterState, useSortingState } from "../../hooks/useScrollPosition";
import { columns } from "./columnsConfig/convertsScanner";

const ConvertsScannerTable = ({
  data,
  title = "Convert Scanner Data",
  globalFilter: initialGlobalFilter = "",
  setGlobalFilter: setParentGlobalFilter,
  sorting: initialSorting = [{ id: "name", desc: false }],
  setSorting: setParentSorting,
  scrollPosition: initialScrollPosition = 0,
  customAction,
  onExpand,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // State Management
  useEffect(() => {
    // On mount / data change, ensure default sort if none set
    if (sorting.length === 0) {
      setSorting([{ id: "name", desc: false }]);
    }
  }, [data]);

  const [savedGlobalFilter, setSavedGlobalFilter] = useFilterState(
    "convertsScannerTableGlobalFilter",
    initialGlobalFilter,
  );
  const [sorting, setSorting] = useSortingState(
    "convertsScannerTableSorting",
    initialSorting,
  );

  // Sync State with Parent
  useEffect(() => {
    if (setParentGlobalFilter) {
      setParentGlobalFilter(savedGlobalFilter);
    }
  }, [savedGlobalFilter, setParentGlobalFilter]);

  useEffect(() => {
    if (setParentSorting) {
      setParentSorting(sorting);
    }
  }, [sorting, setParentSorting]);

  const tableConfig = useMemo(
    () => ({
      enableColumnDragging: false,
      enableSorting: true,
      enableColumnOrdering: false,
      enablePagination: false,
      enableStickyHeader: true,
      enableStickyFooter: false,
      enableColumnResizing: false,
      enableBottomToolbar: false,
      enablePinning: true,
      enableGlobalFilter: true,
      globalFilterFn: "contains",
      enableTopToolbar: false,
      enableColumnActions: false,
      enableRowVirtualization: true,
      sortingDisplay: "none",
      muiTableBodyRowProps: {
        sx: {
          backgroundColor: theme.palette.background.paper,
          "&:nth-of-type(even)": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[900]
                : theme.palette.grey[50],
          },
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        },
      },
      muiTableProps: {
        sx: {
          width: "100%",
          tableLayout: "fixed",
          "& .MuiTableHead-root": {
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: theme.palette.background.paper,
          },
          "& .MuiTableHead-root .MuiTableRow-root": {
            height: "auto",
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            whiteSpace: "normal !important",
            overflow: "visible !important",
            wordBreak: "break-word !important",
            textOverflow: "unset !important",
            lineHeight: 1.4,
            verticalAlign: "top",
            "& *": {
              whiteSpace: "normal !important",
              overflow: "visible !important",
              wordBreak: "break-word !important",
              textOverflow: "unset !important",
            },
            "& .Mui-TableHeadCell-Content": {
              whiteSpace: "normal !important",
              overflow: "visible !important",
              wordBreak: "break-word !important",
              textOverflow: "unset !important",
              width: "100%",
              maxWidth: "100%",
              display: "block !important",
            },
            "& .Mui-TableHeadCell-Content-Labels": {
              whiteSpace: "normal !important",
              overflow: "visible !important",
              wordBreak: "break-word !important",
              textOverflow: "unset !important",
              width: "100%",
              maxWidth: "100%",
              display: "block !important",
            },
            "& .Mui-TableHeadCell-Content-Wrapper": {
              whiteSpace: "normal !important",
              overflow: "visible !important",
              wordBreak: "break-word !important",
              textOverflow: "unset !important",
              width: "100%",
              maxWidth: "100%",
              display: "block !important",
            },
            "& .MuiBox-root": {
              whiteSpace: "normal !important",
              overflow: "visible !important",
              wordBreak: "break-word !important",
              textOverflow: "unset !important",
              width: "100%",
              maxWidth: "100%",
            },
            "& span": {
              whiteSpace: "normal !important",
              overflow: "visible !important",
              wordBreak: "break-word !important",
              textOverflow: "unset !important",
            },
            "& div": {
              whiteSpace: "normal !important",
              overflow: "visible !important",
              wordBreak: "break-word !important",
              textOverflow: "unset !important",
            },
          },
        },
      },
      initialState: {
        // sorting: [{ id: "name", desc: false }],
        density: "compact",
        showColumnFilters: false,
        showGlobalFilter: true,
        globalFilter: savedGlobalFilter,
        columnPinning: isDesktop
          ? {
              left: ["expand", "name", "isin", "underlying_ticker"],
            }
          : {},
      },
      state: {
        globalFilter: savedGlobalFilter,
        sorting: sorting,
      },
      onGlobalFilterChange: setSavedGlobalFilter,
      onSortingChange: setSorting,
      onExpand: onExpand,
    }),
    [theme, isDesktop, savedGlobalFilter, sorting, onExpand],
  );

  // Render
  return (
    <TableContainer
      title={title}
      data={data}
      columnsConfig={columns}
      globalFilter={savedGlobalFilter}
      setGlobalFilter={setSavedGlobalFilter}
      sorting={sorting}
      setSorting={setSorting}
      scrollPosition={null}
      tableConfig={tableConfig}
      customAction={customAction}
    />
  );
};

export default ConvertsScannerTable;
